import React, { useCallback, useContext, useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { parseQueryParams, applyQueryParams } from "../../utils/query-params";
import { fetchPreferredSpecialty, updatePreferredSpecialty } from "../../apis";
import { DEFAULT_SPECIALTY } from "../../constants";

const SpecialtyContext = React.createContext([]);

const SpecialtyProvider = ({ specialties, ...props }) => {
  const [specialty, setSpecialty] = useState(null);
  const [enableStoredPreferences, setEnableStoredPreferences] = useState(false);

  const initialize = async () => {
    const { specialty: specialtyParam, ...otherParams } = parseQueryParams();
    let specialty= specialties.includes(specialtyParam) ? specialtyParam : undefined;

    if (specialty) {
      await updatePreferredSpecialty(specialty);
      setSpecialty(specialty);
      setEnableStoredPreferences(false);
      return;
    }

    // Invalid URL - query params are not empty, but specialty is not present
    if (!isEmpty(otherParams)) {
      specialty = DEFAULT_SPECIALTY;
      await updatePreferredSpecialty(specialty);
      applyQueryParams({ specialty });
      setSpecialty(specialty);
      setEnableStoredPreferences(false);
      return;
    }

    specialty  = await fetchPreferredSpecialty();

    if (!specialty) {
      specialty = DEFAULT_SPECIALTY;
      await updatePreferredSpecialty(specialty);
    }

    applyQueryParams({ specialty }, true);
    setSpecialty(specialty);
    setEnableStoredPreferences(true);
  };

  useEffect(() => {
    initialize().catch((e) => {
      setSpecialty(DEFAULT_SPECIALTY);
      setEnableStoredPreferences(false);
    });
  }, []);

  const handleSetSpecialty = useCallback((newSpecialty) => {
    setSpecialty((prevSpecialty) => {
      if (prevSpecialty !== newSpecialty) {
        updatePreferredSpecialty(newSpecialty);
        // To initialize panel with a specialty filter and prevent stored filters preloading
        applyQueryParams({ specialty: newSpecialty });
      }

      return newSpecialty;
    });
  }, []);

  return (
    <SpecialtyContext.Provider
      {...props}
      value={{
        specialty,
        setSpecialty: handleSetSpecialty,
        enableStoredPreferences
      }}
    />
  );
};

export const useSpecialty = () => {
  const context = useContext(SpecialtyContext);

  if (context === undefined) {
    throw new Error("useSpecialty must be used within a SpecialtyProvider");
  }

  return context;
};

export default SpecialtyProvider;
